import React, { useEffect, useState, useRef } from 'react';
import { Client } from '../types';
import api from '../services/api';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { X, Check, XCircle } from 'lucide-react';

const VIEWS = [
  { value: 'front', label: 'Спереди' },
  { value: 'side', label: 'Сбоку' },
  { value: 'back', label: 'Сзади' },
];

interface ClientPhotoModalProps {
  client: Client;
  isOpen: boolean;
  onClose: () => void;
}

const pad = (n: number) => n.toString().padStart(2, '0');
const getLocalDateString = (date: Date) => {
  const yyyy = date.getFullYear();
  const mm = pad(date.getMonth() + 1);
  const dd = pad(date.getDate());
  return `${yyyy}-${mm}-${dd}`;
};

const Spinner = () => (
  <div className="flex justify-center items-center py-8">
    <div className="animate-spin rounded-full h-10 w-10 border-t-4 border-b-4 border-blue-500"></div>
  </div>
);

const ClientPhotoModal: React.FC<ClientPhotoModalProps> = ({ client, isOpen, onClose }) => {
  const [collageUrl, setCollageUrl] = useState<string | null>(null);
  const [dates, setDates] = useState<string[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedView, setSelectedView] = useState<string>('front');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadDate, setUploadDate] = useState<Date>(new Date());
  const [uploadStatus, setUploadStatus] = useState<{ [key: string]: boolean }>({});

  // Получить даты, где есть все три ракурса
  useEffect(() => {
    if (!client?.id || !isOpen) return;
    api.getClientPhotoDates(client.id).then(async (allDates) => {
      // Для каждой даты — проверить, есть ли все три ракурса
      const validDates: string[] = [];
      for (const d of allDates) {
        const photos = await api.getClientPhotosByDate(client.id, d);
        const views = photos.map((p: any) => p.view);
        if (VIEWS.every(v => views.includes(v.value))) {
          validDates.push(d);
        }
      }
      setDates(validDates);
    }).catch(() => setDates([]));
  }, [client, isOpen]);

  // Получить коллаж (или одно фото)
  const fetchCollage = async (dateStr?: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const blob = await api.getClientPhotoCollage(client.id, dateStr);
      // Проверяем, пустой ли blob (нет фото)
      if (blob.size === 0) {
        setCollageUrl(null);
        setError('NO_PHOTO');
      } else {
        setCollageUrl(URL.createObjectURL(blob));
      }
    } catch (e: any) {
      // Если ошибка 404 или 422, или detail содержит 'Недостаточно фото для коллажа', считаем что фото нет
      const status = e?.response?.status;
      const detail = e?.detail || e?.response?.data?.detail || '';
      if (
        status === 404 ||
        status === 422 ||
        (typeof detail === 'string' && detail.includes('Недостаточно фото для коллажа'))
      ) {
        setCollageUrl(null);
        setError('NO_PHOTO');
      } else {
        setError('Ошибка загрузки фото');
        setCollageUrl(null);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // При открытии или смене даты — обновить коллаж
  useEffect(() => {
    if (!client?.id || !isOpen) return;
    if (selectedDate) {
      fetchCollage(getLocalDateString(selectedDate));
    } else {
      fetchCollage();
    }
    // eslint-disable-next-line
  }, [client, isOpen, selectedDate]);

  // Проверить статус загрузки для выбранной даты
  const checkUploadStatus = async (date: Date) => {
    const dateStr = getLocalDateString(date);
    try {
      const photos = await api.getClientPhotosByDate(client.id, dateStr);
      const status: { [key: string]: boolean } = {};
      VIEWS.forEach(view => {
        status[view.value] = photos.some((p: any) => p.view === view.value);
      });
      setUploadStatus(status);
    } catch (error) {
      setUploadStatus({});
    }
  };

  // Обновить статус при изменении даты загрузки
  useEffect(() => {
    if (client?.id && uploadDate) {
      checkUploadStatus(uploadDate);
    }
  }, [client?.id, uploadDate]);

  // Загрузка нового фото
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'image/png' && file.type !== 'image/jpeg') {
      setError('Только PNG или JPG-файлы поддерживаются');
      return;
    }
    setUploading(true);
    setError(null);
    try {
      const dateStr = getLocalDateString(uploadDate);
      await api.uploadClientPhotoWithView(client.id, file, selectedView, dateStr);
      setSelectedDate(null); // сбросить выбранную дату
      // обновить даты
      api.getClientPhotoDates(client.id).then(async (allDates) => {
        const validDates: string[] = [];
        for (const d of allDates) {
          const photos = await api.getClientPhotosByDate(client.id, d);
          const views = photos.map((p: any) => p.view);
          if (VIEWS.every(v => views.includes(v.value))) {
            validDates.push(d);
          }
        }
        setDates(validDates);
      });
      await fetchCollage();
      // Обновить статус загрузки
      await checkUploadStatus(uploadDate);
      
      // Автоматически выбрать следующий незагруженный ракурс
      // Получаем актуальный статус после загрузки
      const photos = await api.getClientPhotosByDate(client.id, dateStr);
      const currentStatus: { [key: string]: boolean } = {};
      VIEWS.forEach(view => {
        currentStatus[view.value] = photos.some((p: any) => p.view === view.value);
      });
      
      const nextUnloadedView = VIEWS.find(view => !currentStatus[view.value]);
      if (nextUnloadedView) {
        setSelectedView(nextUnloadedView.value);
      }
      
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Ошибка загрузки файла');
    } finally {
      setUploading(false);
    }
  };

  // Для выделения дат с фото в календаре
  const highlightDates = dates.map(d => new Date(d));

  if (!isOpen) return null;

  // Вычисление дат сравнения для имени файла
  let date1 = '';
  let date2 = '';
  if (selectedDate) {
    date1 = getLocalDateString(selectedDate);
    // Найти последнюю дату с фото (dates[0] — самая свежая)
    date2 = dates.length > 0 ? dates[0] : date1;
  } else if (dates.length > 1) {
    date2 = dates[0];
    date1 = dates[1];
  } else if (dates.length === 1) {
    date1 = date2 = dates[0];
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="relative w-full max-w-lg h-full max-h-screen sm:h-auto sm:max-h-[90vh] flex flex-col">
        <div className="bg-white rounded-lg shadow-lg flex-1 p-0 sm:p-0 overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Фото клиента: {client.first_name} {client.last_name}</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
              <X size={24} />
            </button>
          </div>
          <div className="p-2 sm:p-6">
            {isLoading ? (
              <Spinner />
            ) : error === 'NO_PHOTO' ? (
              <div className="text-gray-400 text-center py-8">У клиента еще нет загруженных фото</div>
            ) : error ? (
              <div className="text-red-500 text-center py-8">{error}</div>
            ) : collageUrl ? (
              <div className="flex flex-col items-center">
                <img
                  src={collageUrl}
                  alt="Фото клиента"
                  className="max-w-full max-h-96 rounded shadow mb-4 cursor-pointer"
                  onClick={() => window.open(collageUrl, '_blank')}
                  title="Открыть полноразмерное изображение"
                />
                <a
                  href={collageUrl}
                  download={`client_${client.id}_collage_${date1}_${date2}.jpg`}
                  className="text-blue-600 hover:underline text-sm mt-2"
                >
                  Скачать коллаж
                </a>
              </div>
            ) : (
              <div className="text-gray-400 text-center py-8">Нет фото</div>
            )}
            <div className="mt-4">
              <div className="mb-2 text-sm font-medium">Сравнить с:</div>
              <DatePicker
                selected={selectedDate}
                onChange={(date: Date | null) => setSelectedDate(date)}
                highlightDates={highlightDates}
                includeDates={highlightDates}
                placeholderText="Выберите дату..."
                dateFormat="yyyy-MM-dd"
                className="border rounded px-2 py-1 w-full"
                isClearable
              />
            </div>
            <div className="mt-6">
              <div className="mb-2 text-sm font-medium">Добавить новое фото (png/jpeg):</div>
              <div className="flex items-center space-x-2 mb-2">
                <select
                  value={selectedView}
                  onChange={e => setSelectedView(e.target.value)}
                  className="border rounded px-2 py-1"
                  disabled={uploading}
                >
                  {VIEWS.map(v => (
                    <option key={v.value} value={v.value}>{v.label}</option>
                  ))}
                </select>
                <DatePicker
                  selected={uploadDate}
                  onChange={date => setUploadDate(date as Date)}
                  dateFormat="yyyy-MM-dd"
                  className="border rounded px-2 py-1"
                  disabled={uploading}
                  maxDate={new Date()}
                />
              </div>
              
              {/* Статус загрузки для выбранной даты */}
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <div className="text-sm font-medium text-gray-700 mb-2">
                  Статус загрузки на {uploadDate.toLocaleDateString('ru-RU')}:
                </div>
                <div className="flex flex-col space-y-2">
                  {VIEWS.map(view => (
                    <div key={view.value} className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">{view.label}:</span>
                      <div className="flex items-center">
                        {uploadStatus[view.value] ? (
                          <Check className="h-5 w-5 text-green-500" />
                        ) : (
                          <XCircle className="h-5 w-5 text-red-500" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="mb-2">
                <input
                  type="file"
                  accept="image/png, image/jpeg"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  disabled={uploading}
                  className="block text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                />
              </div>
              {uploading && <Spinner />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientPhotoModal; 